import { useState } from "react";
import { Link } from "react-router-dom";
import Footer from "@/components/site/Footer";
import BookingSidebar from "@/components/site/BookingSidebar";
import hero from "@/assets/tour-cambodia-10day-treeroots.jpg";

const itinerary = [
  { key: "01", label: "Day 1", time: "7:30 AM – 5:30 PM", title: "Angkor Wat Sunrise & Small Circuit", body: "Sunrise at Angkor Wat, then Angkor Thom, Bayon and Ta Prohm. Overnight: 4–5★ hotel in Siem Reap." },
  { key: "02", label: "Day 2", time: "8:00 AM – 5:30 PM", title: "Banteay Srei & Grand Circuit", body: "Visit Banteay Srei and the Grand Circuit temples Preah Khan, Neak Pean, Ta Som. Overnight: Siem Reap." },
  { key: "03", label: "Day 3", time: "8:00 AM – 2:00 PM", title: "Kampong Phluk Floating Village", body: "Boat trip to Kampong Phluk explore the floating village and stilt houses. Overnight: Siem Reap." },
  { key: "04", label: "Day 4", time: "7:30 AM – 5:30 PM", title: "Phnom Kulen National Park", body: "Full-day Phnom Kulen 1000 Lingas, the Giant Buddha and the waterfall. Overnight: Siem Reap." },
  { key: "05", label: "Day 5", time: "7:30 AM – 6:30 PM", title: "Beng Mealea & Koh Ker", body: "Visit the jungle ruin of Beng Mealea and continue to the pyramid of Koh Ker. Overnight: Siem Reap." },
  { key: "06", label: "Day 6", time: "7:30 AM – 5:30 PM", title: "Spean Toap & Banteay Chhmar", body: "Travel to Spean Toap (ancient Angkor bridge), continue to Banteay Chhmar temple and village life. Overnight: boutique eco-lodge near Banteay Chhmar." },
  { key: "07", label: "Day 7", time: "8:00 AM – 6:00 PM", title: "Transfer to Battambang", body: "Transfer to Battambang with a countryside tour and stops in local villages. Overnight: 4★ hotel in Battambang." },
  { key: "08", label: "Day 8", time: "8:00 AM – 6:00 PM", title: "Bamboo Train & Bat Caves", body: "Bamboo Train experience, the Killing Caves and sunset bat watching. Overnight: Battambang." },
  { key: "09", label: "Day 9", time: "7:30 AM – 3:00 PM", title: "Transfer to Phnom Penh", body: "Transfer to Phnom Penh city highlights and the riverside. Overnight: 4–5★ hotel in Phnom Penh." },
  { key: "10", label: "Day 10", time: "8:00 AM – 3:00 PM", title: "Royal Palace & Departure", body: "Visit the Royal Palace, Silver Pagoda, National Museum and the Killing Fields, then transfer to Phnom Penh Airport." },
];

const Timeline = ({ items }: { items: typeof itinerary }) => (
  <ol className="space-y-px">
    {items.map((s, idx) => (
      <li
        key={s.key}
        className="group grid grid-cols-[auto_1fr] md:grid-cols-[200px_1fr] gap-x-4 md:gap-x-12 min-w-0 border-t border-border py-8 md:py-10 transition-colors hover:bg-secondary/40 last:border-b"
      >
        <div className="flex flex-col items-start gap-y-2 text-base">
          <span className="font-display text-primary tabular-nums text-xl">
            {String(idx + 1).padStart(2, "0")}
          </span>
          <span className="uppercase tracking-[0.25em] text-primary text-xs md:text-base">
            {s.time}
          </span>
        </div>
        <div>
          <h3 className="font-display text-2xl md:text-3xl leading-tight mb-3 transition-colors group-hover:text-primary">
            {s.title}
          </h3>
          <p className="text-muted-foreground font-light leading-relaxed text-lg max-w-2xl">
            {s.body}
          </p>
        </div>
      </li>
    ))}
  </ol>
);

const optionOne = {
  label: "OPTION 1 ESSENTIALS",
  pricePrivate: "$950 – $1,250 total (1–2 pax)",
  priceGroup: "$495 per person",
  includes: [
    "Air-conditioned transportation with hotel pickup and drop-off",
    "Complimentary bottled water",
    "Refreshing cold towels",
    "Photography guidance for the best angles and lighting",
  ],
  excludes: [
    "Professional English-speaking guide with expert local knowledge",
    "All accommodation",
    "Personal expenses",
    "All travel insurance",
    "All meals",
  ],
};

const optionTwo = {
  label: "OPTION 2 ALL-INCLUSIVE",
  pricePrivate: "$2,350 – $2,750 total (1–2 pax)",
  priceGroup: "$950 per person",
  includes: [
    "Accommodation in 4★ hotels",
    "All entrance fees",
    "Professional English-speaking guide with expert local knowledge",
    "Air-conditioned transportation with hotel pickup and drop-off",
    "Lunches from Day 2 to Day 10",
    "1 dinner show with traditional Khmer dance",
    "Complimentary bottled water",
    "Refreshing cold towels",
    "Photography guidance for the best angles and lighting",
  ],
  excludes: ["Tips and gratuities", "Personal expenses", "Travel insurance"],
};

const TourCambodia10Day = () => {
  const [option, setOption] = useState(0);
  const opt = option === 0 ? optionOne : optionTwo;
  return (
    <main className="bg-background">
      {/* HERO */}
      <header className="relative bg-jungle text-background overflow-hidden">
        <img
          src={hero}
          alt="Cambodia private tour with French-speaking guide among the giant tree roots of Ta Prohm temple, Angkor"
          width={1920}
          height={1280}
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-jungle/70 via-jungle/50 to-jungle/95" />
        <div className="relative z-10">
          <div className="container flex items-center justify-between py-6">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-display text-2xl tracking-wide">Aṅkor</span>
              <span className="text-[10px] uppercase tracking-[0.3em] opacity-70 hidden sm:inline">Sojourns</span>
            </Link>
            <Link to="/tours" className="text-[11px] uppercase tracking-[0.3em] border-b border-background/40 pb-1 hover:border-primary hover:text-primary transition-colors">
              ← All Tours
            </Link>
          </div>
          <div className="container pt-20 pb-32 md:pt-28 md:pb-44 max-w-5xl">
            <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Ten Day Grand Discovery</p>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95] text-balance lg:text-2xl">
              10 Days Across <em className="font-light">Cambodia</em>&nbsp;Siem Reap, Banteay Chhmar, Battambang &amp; Phnom Penh.
            </h1>
            <p className="mt-10 max-w-xl text-background/70 leading-relaxed text-xl font-light">
              Discover Cambodia's highlights on this 10-day journey, from the world-famous temples of Angkor Wat to remote heritage sites, floating villages, countryside life, and the vibrant capital Phnom Penh. A tour that combines history, culture and authentic local experiences.
            </p>
            <div className="mt-10 flex flex-wrap gap-x-10 gap-y-3 text-[11px] uppercase tracking-[0.3em] text-background/80">
              <span>Duration · 10 Days</span>
              <span>Group · 1–15</span>
              <span>From $495 / pax</span>
            </div>
          </div>
        </div>
      </header>

      {/* INTRO */}
      <section className="py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— The Idea</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-10 text-balance">
            Four chapters of Cambodia, ten unhurried days.
          </h2>
          <div className="space-y-6 text-lg font-light text-muted-foreground leading-relaxed">
            <p>
              Begin with five days at Angkor and Tonlé Sap, push west to the moated city of Banteay Chhmar, slow down in Battambang for bamboo trains and bat caves, and finish in Phnom Penh the Royal Palace, the Silver Pagoda and a sober afternoon at the Killing Fields.
            </p>
          </div>
        </div>
      </section>

      {/* ITINERARY */}
      <section className="pb-24 md:pb-32">
        <div className="container max-w-6xl"><div className="grid lg:grid-cols-[1fr_420px] gap-x-12 gap-y-12 items-start"><div>
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Itinerary</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-16 text-balance">
            Ten days, hour by hour.
          </h2>
          <Timeline items={itinerary} />
        </div><BookingSidebar pricePerPerson={915} tourTitle="10 Days Tour Across Cambodia" pricingTiers={[{ maxPax: 2, price: 915 }, { maxPax: 6, price: 1350 }, { maxPax: 10, price: 1759 }]} /></div></div>
      </section>

      {/* PRICING TABS */}
      <section className="bg-secondary py-24 md:py-32">
        <div className="container max-w-5xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Pricing &amp; Inclusions</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-12 text-balance">
            Choose your level of comfort.
          </h2>

          <div role="tablist" aria-label="Pricing options" className="flex justify-center gap-10 md:gap-16 mb-16">
            {[optionOne, optionTwo].map((o, i) => {
              const isActive = i === option;
              return (
                <button
                  key={o.label}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setOption(i)}
                  className={`relative pb-3 uppercase tracking-[0.3em] transition-colors text-base ${
                    isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {o.label}
                  <span
                    className={`absolute left-0 right-0 -bottom-px h-px transition-colors ${
                      isActive ? "bg-primary" : "bg-transparent"
                    }`}
                  />
                </button>
              );
            })}
          </div>

          <ul className="mb-12 space-y-4 font-light text-lg max-w-3xl mx-auto">
            <li className="border-b border-border pb-4 flex justify-between gap-6"><span>Private Tour (10 Days)</span><span className="text-right">{opt.pricePrivate}</span></li>
            <li className="pb-4 flex justify-between gap-6"><span>Group Tour</span><span className="text-right">{opt.priceGroup}</span></li>
          </ul>

          <div className="grid md:grid-cols-2 gap-x-12 gap-y-12">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Includes</p>
              <ul className="space-y-4 font-light text-lg">
                {opt.includes.map((x) => (
                  <li key={x} className="border-b border-border pb-4 last:border-b-0">{x}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Excludes</p>
              <ul className="space-y-4 font-light text-lg">
                {opt.excludes.map((x) => (
                  <li key={x} className="border-b border-border pb-4 last:border-b-0">{x}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT TO BRING */}
      <section className="py-24 md:py-32">
        <div className="container max-w-5xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Essential Items</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-16 text-balance">What to bring, what to leave behind.</h2>
          <div className="grid md:grid-cols-3 gap-x-10 gap-y-12">
            <div>
              <h3 className="font-display text-2xl mb-5">Required</h3>
              <ul className="space-y-3 font-light text-muted-foreground">
                <li>Flashlight or phone light</li>
                <li>Modest clothing shoulders &amp; knees covered</li>
                <li>Comfortable walking shoes</li>
              </ul>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-5">Recommended</h3>
              <ul className="space-y-3 font-light text-muted-foreground">
                <li>Camera with extra batteries</li>
                <li>Small backpack</li>
                <li>Cash for breakfast</li>
                <li>Sun protection hat, sunscreen</li>
              </ul>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-5">Leave at Hotel</h3>
              <ul className="space-y-3 font-light text-muted-foreground">
                <li>Passport (bring a copy)</li>
                <li>Airline tickets</li>
                <li>Excess cash</li>
                <li>Valuable jewellery</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* GOOD TO KNOW */}
      <section className="bg-secondary py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Good to Know</p>
          <div className="space-y-6 text-lg font-light text-muted-foreground leading-relaxed">
            <p>Sunrise tours are the most popular and tend to sell out quickly, especially during the dry season (November to March). We recommend booking at least 48 hours in advance during peak season, even earlier.</p>
            <p>Pickup time: 7:30 – 8:00 AM (exact time confirmed one day before). Daily tours typically end between 1:30 PM and 6:30 PM depending on the day.</p>
            <p>Breakfast: if your hotel includes breakfast, request a takeaway box, or stop at a local restaurant.</p>
            <p>Dress code: casual but respectful covered shoulders and knees required inside temples. Comfortable walking shoes are strongly recommended.</p>
            <p>For safety, leave valuables at your hotel and bring only what you need. This tour is not wheelchair accessible. Children under 5 are not permitted. Maximum group size: 1–15 travellers in an air-conditioned vehicle.</p>
            <p><span className="text-foreground">Weather.</span> Hot and dry throughout. There is always the possibility of a shower or two, but it is likely to be dry most of the time. Midday temperatures are likely to be very hot, but most of the woodland sites we visit have low humidity.</p>
            <p><span className="text-foreground">Food.</span> Khmer cuisine is similar to most Southeast Asian cooking, but rather less spicy. Good bread is available in many areas thanks to the French influence, and there is a surprisingly wide range of local brews to try.</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="container max-w-3xl text-center">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Reserve Your Journey</p>
          <h2 className="font-display text-4xl md:text-6xl leading-[1.05] mb-10 text-balance">
            Ten days. From Angkor to the Mekong.
          </h2>
          <Link
            to="/#contact"
            className="inline-block bg-foreground text-background px-10 py-4 text-[11px] uppercase tracking-[0.3em] hover:bg-primary transition-colors"
          >
            Book Now →
          </Link>
        </div>
      </section>
      <Footer />
    </main>
  );
};

export default TourCambodia10Day;
